import React, { useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { usePortal } from '../../context/PortalContext';
import StatEntry from './tabs/StatEntry';
import MyStats from './tabs/MyStats';
import Patrols from './tabs/Patrols';
import DepartmentStats from './tabs/DepartmentStats';
import AdminDashboard from '../AdminDashboard/AdminDashboard';
import styles from './OfficerDashboard.module.css';

/**
 * OfficerDashboard
 * 
 * Main portal component. Shows:
 * - Officer tabs (Stat Entry, My Stats, Patrols, Department Stats)
 * - Admin tabs (if is_admin)
 * - Tab router
 */
function OfficerDashboard() {
  const { is_admin, allowed_admin_tabs, has_view_stats_permission, can_view_department_stats, logout_url, officer_id } = useAuth();
  const { state: portalState } = usePortal();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('stat-entry');

  const officerIdentity = useMemo(() => {
    const currentOfficer = (portalState.officers || []).find((officer) => String(officer.id) === String(officer_id));
    if (!currentOfficer) {
      return '';
    }

    const rawRank = currentOfficer.rank ?? '';
    const matchedRank = (portalState.ranks || []).find((rank) => String(rank.id) === String(rawRank));
    const rankLabel = (matchedRank?.name || rawRank || '').trim();
    const nameLabel = (currentOfficer.name || '').trim();

    return [rankLabel, nameLabel].filter(Boolean).join(' ');
  }, [officer_id, portalState.officers, portalState.ranks]);

  const allowedAdminSlugs = Array.isArray(allowed_admin_tabs) ? [...allowed_admin_tabs] : [];
  if (is_admin && !allowedAdminSlugs.includes('view-stats')) allowedAdminSlugs.push('view-stats');
  const showDepartmentStats = can_view_department_stats || (is_admin && has_view_stats_permission);

  const officerTabs = [
    { id: 'stat-entry', label: 'Stat Entry', component: StatEntry },
    { id: 'my-stats', label: 'My Stats', component: MyStats },
    { id: 'patrols', label: 'Patrols', component: Patrols },
    ...(showDepartmentStats ? [{ id: 'dept-stats', label: 'Department Stats', component: DepartmentStats }] : []),
  ];

  const adminTabMap = {
    'general': { id: 'general', label: 'General Settings', component: AdminDashboard },
    'officers-categories': { id: 'officers', label: 'Officers, Categories, & Shifts', component: AdminDashboard },
    'patrols': { id: 'patrols-admin', label: 'Patrol Requests', component: AdminDashboard },
    'view-stats': { id: 'view-stats', label: 'View Stats', component: AdminDashboard },
    'appearance': { id: 'appearance', label: 'Appearance', component: AdminDashboard },
    'change-requests': { id: 'change-requests', label: 'Change Requests', component: AdminDashboard },
    'api-views': { id: 'api-views', label: 'API Views', component: AdminDashboard },
    'logs': { id: 'logs', label: 'Logs', component: AdminDashboard },
    'documentation': { id: 'docs', label: 'Documentation', component: AdminDashboard },
  };

  const adminTabs = is_admin
    ? allowedAdminSlugs
        .map((slug) => adminTabMap[slug])
        .filter(Boolean)
    : [];

  const allTabs = [...officerTabs, ...adminTabs];

  const activeIndex = Math.max(allTabs.findIndex((tab) => tab.id === activeTab), 0);
  
  // Get active tab config
  const activeTabConfig = allTabs.find((tab) => tab.id === activeTab);
  const TabComponent = activeTabConfig?.component || StatEntry;

  const onTabKeyDown = (event) => {
    if (!allTabs.length) return;

    let nextIndex = activeIndex;

    if (event.key === 'ArrowRight') {
      nextIndex = (activeIndex + 1) % allTabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (activeIndex - 1 + allTabs.length) % allTabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = allTabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    setActiveTab(allTabs[nextIndex].id);
  };

  const warningMessage = (portalState.generalSettings?.warning_message || '').trim();

  return (
    <div className={styles.container}>
      {warningMessage ? (
        <div className={styles.warningBanner} role="alert">
          {warningMessage}
        </div>
      ) : null}
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>Law Enforcement Portal</h1>
          {officerIdentity ? <p className={styles.subtitle}>{officerIdentity}</p> : null}
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.themeToggle}
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
          </button>
          {logout_url ? (
            <a
              className={styles.logoutButton}
              href={logout_url}
              aria-label="Logout"
              title="Logout"
            >
              Logout
            </a>
          ) : null}
        </div>
      </header>

      <nav className={styles.tabs} role="tablist" aria-label="Portal sections" aria-orientation="horizontal">
        {allTabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id)}
            onKeyDown={onTabKeyDown}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            id={`tab-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main
        className={styles.tabContent}
        role="tabpanel"
        id={`panel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        <TabComponent activeTab={activeTab} />
      </main>
    </div>
  );
}

export default OfficerDashboard;
